import { Accordion, Alert, Badge, Button, Card, Code, Group, Stack, Table, Text, Title } from "@mantine/core";
import { IconSettings, IconShieldLock } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router";

import { http } from "../../api/http";
import { useCan } from "../../auth/user";
import { fmtDateTime, PageHeader } from "../../components/ui";
import { useT } from "../../i18n/script";
import AdminLayout from "./AdminLayout";
import type { SettingSection } from "./SettingsPage";

interface A { code: string; name: string; purpose: string; legal_basis: string; data_subjects: string; data_categories: string; recipients?: string;
  retention?: string; security_measures?: string; module?: string; updated_at: string }

export default function PrivacyPage() {
  const { t } = useT();
  const can = useCan();
  const [sp] = useSearchParams();
  const acts = useQuery({ queryKey: ["privacy"], enabled: can("audit:read"), queryFn: () => http.get<A[]>("/api/v1/privacy/activities") });
  const mine = useQuery({ queryKey: ["my-data"], queryFn: () => http.get<Record<string, unknown>>("/api/v1/privacy/my-data") });
  return (
    <AdminLayout>
      <Stack>
        <PageHeader title="Zaštita ličnih podataka" description="Evidencija aktivnosti obrade ličnih podataka i pravo ispitanika na pristup (Zakon o zaštiti ličnih podataka BiH, „Sl. glasnik BiH“ 12/25)." />
        <Alert icon={<IconShieldLock />} color="blue" variant="light">
          {t("Sadržaj evidencije je početni prijedlog izrađen uz sistem — službenik za zaštitu ličnih podataka Agencije ga mora pregledati i potvrditi (pravni osnov, rokovi čuvanja).")}
        </Alert>
        {can("system:params") && <PrivacyRulesCard />}
        <Card withBorder style={sp.get("moji") ? { borderColor: "var(--mantine-color-agri-6)" } : undefined}>
          <Title order={5} mb="xs">{t("Moji podaci u sistemu")}</Title>
          <Code block>{JSON.stringify(mine.data ?? {}, null, 2)}</Code>
        </Card>
        {can("audit:read") && (
          <Card withBorder>
            <Title order={5} mb="xs">{t("Evidencija aktivnosti obrade")}</Title>
            <Accordion variant="separated">
              {(acts.data ?? []).map((a) => (
                <Accordion.Item key={a.code} value={a.code}>
                  <Accordion.Control><Group><Text fw={600}>{a.name}</Text>{a.module && <Badge size="xs" variant="light">{a.module}</Badge>}</Group></Accordion.Control>
                  <Accordion.Panel>
                    {([["Svrha", a.purpose], ["Pravni osnov", a.legal_basis], ["Kategorije ispitanika", a.data_subjects], ["Kategorije podataka", a.data_categories],
                      ["Primaoci", a.recipients], ["Rok čuvanja", a.retention], ["Mjere zaštite", a.security_measures]] as [string, string | undefined][]).map(([k, v]) => (
                      <Text key={k} size="sm" mb={4}><b>{t(k)}:</b> {v ?? "—"}</Text>
                    ))}
                    <Text size="xs" c="dimmed">{t("Ažurirano")}: {fmtDateTime(a.updated_at)}</Text>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          </Card>
        )}
      </Stack>
    </AdminLayout>
  );
}

/** Trenutna pravila zaštite (sekcija „privatnost“ iz podešavanja) — samo pregled, izmjena u Podešavanjima. */
function PrivacyRulesCard() {
  const { t } = useT();
  const nav = useNavigate();
  const q = useQuery({ queryKey: ["settings"], queryFn: () => http.get<SettingSection[]>("/api/v1/settings") });
  const sec = q.data?.find((x) => x.code === "privatnost");
  return (
    <Card withBorder radius="lg">
      <Group justify="space-between" mb="xs" wrap="wrap">
        <div>
          <Title order={5}>{t("Pravila zaštite (podesiva)")}</Title>
          <Text size="xs" c="dimmed">{t("Važe odmah za sve korisnike; svaka izmjena se bilježi u istoriji aktivnosti.")}</Text>
        </div>
        <Button size="xs" variant="light" leftSection={<IconSettings size={14} />} onClick={() => nav("/admin/podesavanja?sekcija=privatnost")}>
          {t("Izmijeni pravila")}
        </Button>
      </Group>
      {q.isLoading ? <Text size="sm" c="dimmed">…</Text> : !sec ? <Text size="sm" c="dimmed">{t("Podešavanja nisu dostupna.")}</Text> : (
        <Table verticalSpacing={6} fz="sm">
          <Table.Tbody>
            {sec.items.map((i) => {
              const v = i.value;
              const label = (x: unknown) => i.options?.find((o) => String(o.value) === String(x))?.label ?? String(x);
              return (
                <Table.Tr key={i.key}>
                  <Table.Td w="45%"><Text size="sm">{t(i.label)}</Text>{i.help && <Text size="xs" c="dimmed">{t(i.help)}</Text>}</Table.Td>
                  <Table.Td>
                    {typeof v === "boolean" ? <Badge variant="light" color={v ? "green" : "gray"}>{v ? t("uključeno") : t("isključeno")}</Badge>
                      : Array.isArray(v) ? (v.length ? <Group gap={4}>{v.map((x) => <Badge key={String(x)} size="sm" variant="light" color="gray" tt="none">{label(x)}</Badge>)}</Group>
                        : <Text size="sm" c="dimmed">{t("niko")}</Text>)
                      : i.type === "textarea" ? <Text size="sm" lineClamp={3}>{String(v ?? "—")}</Text>
                      : <Text size="sm" fw={600}>{v == null || v === "" ? "—" : `${i.options ? label(v) : String(v)}${i.unit ? ` ${t(i.unit)}` : ""}`}</Text>}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      )}
    </Card>
  );
}
